import '../Button.scss';

import { PopupApiDto } from '@b2x/storefront-api-js-client/src/dto';
import classnames from 'classnames';
import React from 'react';

import { AssetAsBackgroundV2 } from '../AssetAsBackgroundV2';
import { Col, ResponsiveColSize } from '../Col';
import { BackgroundContentSection, PopupContentTypeV2, ResponsiveColumnSizeContentSection } from '../contentTypes';
import { Div } from '../HTMLElement';
import { AlignItems, JustifyContent } from '../interfaces';
import { Row } from '../Row';
import {
  buildResponsiveGap,
  buildSizeClassNamesFromContent,
  buildSpacingClassNamesFromContent,
  buildVisibilityClassNamesFromContent,
} from '../util';
import { PropsWithCustomComponent, VariantsController } from '../VariantsController';
import { PopupBuilderAssetComponent } from './PopupBuilderAssetComponent';
import { PopupBuilderButtonComponent } from './PopupBuilderButtonComponent';
import { PopupBuilderCountdownComponent } from './PopupBuilderCountdownComponent';
import { PopupBuilderCouponDetailsComponent } from './PopupBuilderCouponDetailsComponent';
import { PopupBuilderCustomComponent } from './PopupBuilderCustomComponent';
import { PopupBuilderNewsletterComponent } from './PopupBuilderNewsletterComponent';
import { PopupBuilderProductComponent } from './PopupBuilderProductComponent';
import { PopupBuilderRichTextComponent } from './PopupBuilderRichTextComponent';
import { PopupBuilderTextComponent } from './PopupBuilderTextComponent';

export interface PopupBuilderProps {
  popup: PopupApiDto<PopupContentTypeV2>;
}

const PopupBuilder = ({ popup }: PopupBuilderProps) => {
  const mapResponsiveColumnSize = (input: ResponsiveColumnSizeContentSection): ResponsiveColSize => {
    const breakpoints = ['xs', 'sm', 'md', 'lg', 'xl', 'xxl'] as const;

    const output: ResponsiveColSize = {};

    for (const bp of breakpoints) {
      const section = input[bp];
      output[bp] = section?.columnSize || undefined;
    }

    return output;
  };

  const popupContent = popup.content?.body;
  const popupTextColor = popupContent?.options?.textColor?.at(0);

  return (
    <Div className="popup-builder" style={{ color: popupTextColor?.hex }} textVariant={popupTextColor?.variant}>
      {(popupContent?.options?.background?.asset?.type || popupContent?.options?.background?.variant) && (
        <PopupBackground {...popupContent.options.background} />
      )}
      {popupContent?.blocks && popupContent.blocks.length > 0 && (
        <Div style={{ position: 'relative' }}>
          <Row>
            {popupContent.blocks.map((block) => {
              const enabled = block.options?.enabled;
              const size = block.options?.size && mapResponsiveColumnSize(block.options.size);
              const gap = block.options?.gap && buildResponsiveGap(block.options.gap);
              const offset = buildSizeClassNamesFromContent('offset', block.options?.offset);
              const margin = buildSpacingClassNamesFromContent('m', block.options?.margin);
              const padding = buildSpacingClassNamesFromContent('p', block.options?.padding);
              const justifyContent = block.options?.justifyContent as JustifyContent;
              const alignItems = block.options?.alignItems as AlignItems;
              const textColor = block.options?.textColor?.at(0);
              const visibility = block.options?.visibility;
              const visibilityClassNames = visibility && buildVisibilityClassNamesFromContent(visibility);

              return (
                <React.Fragment key={block.contentSectionId}>
                  {enabled && (
                    <Col size={size} uiClassName={{ bs5: classnames(offset) }}>
                      <Div
                        display="grid"
                        style={{ color: textColor?.hex, position: 'relative' }}
                        textVariant={textColor?.variant}
                        uiClassName={{ bs5: classnames(margin, padding, 'h-100', visibilityClassNames) }}
                      >
                        {(block.options?.background?.asset?.type || block.options?.background?.variant) && (
                          <PopupBackground {...block.options.background} />
                        )}
                        <Div
                          alignItems={alignItems}
                          display="flex"
                          flexDirection="column"
                          gap={gap}
                          justifyContent={justifyContent}
                          style={{ position: 'relative' }}
                        >
                          {block.component?.map((singleBlock) => (
                            <React.Fragment key={singleBlock.contentSectionId}>
                              {singleBlock.text?.options?.enabled && (
                                <PopupBuilderTextComponent {...singleBlock.text} />
                              )}
                              {singleBlock.richText?.options?.enabled && (
                                <PopupBuilderRichTextComponent {...singleBlock.richText} />
                              )}
                              {singleBlock.asset?.options?.enabled && (
                                <PopupBuilderAssetComponent {...singleBlock.asset} />
                              )}
                              {singleBlock.custom?.options?.enabled && (
                                <PopupBuilderCustomComponent {...singleBlock.custom} />
                              )}
                              {singleBlock.newsletter?.options?.enabled && (
                                <PopupBuilderNewsletterComponent {...singleBlock.newsletter} />
                              )}
                              {singleBlock.countdown?.options?.enabled && popup.countdownDate && (
                                <PopupBuilderCountdownComponent
                                  {...singleBlock.countdown}
                                  expiration={popup.countdownDate}
                                />
                              )}
                              {singleBlock.product?.options?.enabled && popup.productId && (
                                <PopupBuilderProductComponent
                                  {...singleBlock.product}
                                  productId={popup.productId.toString()}
                                />
                              )}
                              {singleBlock.button?.options?.enabled && (
                                <PopupBuilderButtonComponent {...singleBlock.button} popup={popup} />
                              )}
                              {singleBlock.couponDetails?.options?.enabled && (
                                <PopupBuilderCouponDetailsComponent {...singleBlock.couponDetails} popup={popup} />
                              )}
                            </React.Fragment>
                          ))}
                        </Div>
                      </Div>
                    </Col>
                  )}
                </React.Fragment>
              );
            })}
          </Row>
        </Div>
      )}
    </Div>
  );
};

export type PopupBuilderVariants = '';

const PopupBuilderController = (props: PropsWithCustomComponent<PopupBuilderProps>) => (
  <VariantsController<PopupBuilderProps, PopupBuilderVariants>
    {...props}
    variantsControllerConfig={{
      defaultComponent: PopupBuilder,
      name: 'PopupBuilder',
    }}
  />
);
export { PopupBuilderController as PopupBuilder };

const PopupBackground = ({ asset, variant }: BackgroundContentSection) => {
  return (
    <Div
      className={classnames('background', { [`bg-${variant}`]: variant })}
      style={{ height: '100%', left: 0, position: 'absolute', top: 0, width: '100%' }}
    >
      {asset && <AssetAsBackgroundV2 {...asset} />}
    </Div>
  );
};
